import { useState } from 'react';
import { Zap, X, AlertTriangle, CheckCircle, Info, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StoryAuditor({ projectId, projectType, isPro, onClose }) {
  const [loading, setLoading] = useState(false);
  const [auditResults, setAuditResults] = useState(null);

  const runAudit = async () => {
    if (!isPro) {
      toast.error('Story Auditor is a Pro feature');
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`${API}/story/audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId,
          project_type: projectType,
        }),
      });

      if (response.status === 403) {
        toast.error('Upgrade to Pro to use Story Auditor');
        return;
      }

      if (!response.ok) throw new Error('Audit failed');

      const data = await response.json();
      setAuditResults(data);
      toast.success('Story audit complete');
    } catch (error) {
      console.error('Audit error:', error);
      toast.error('Failed to run story audit');
    } finally {
      setLoading(false);
    }
  };

  if (!auditResults && !loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
        <div className="bg-card border border-primary/30 p-8 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Zap className="w-6 h-6 text-primary" />
              <h3 className="font-mono text-xl font-semibold">Story Auditor</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <p className="text-muted-foreground mb-6">
            The Story Auditor will analyze your {projectType === 'character' ? 'character arc' : 'plot structure'} and provide feedback on:
          </p>

          <ul className="space-y-3 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm">Consistency of character motivations and arc</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm">Plot beat alignment with structure</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm">Enneagram stress pattern integration</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm">Relationship dynamics and conflicts</span>
            </li>
          </ul>

          <Button
            onClick={runAudit}
            disabled={loading || !isPro}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 shadow-glow"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Run Stress Test
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="bg-card border border-primary/30 p-12 text-center">
          <Loader2 className="w-12 h-12 text-primary mx-auto mb-6 animate-spin" />
          <p className="font-mono text-lg">Auditing your story...</p>
          <p className="text-muted-foreground text-sm mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-card border border-primary/30 p-8 max-w-2xl w-full mx-4 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-primary" />
            <h3 className="font-mono text-xl font-semibold">Audit Results</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Overall Score */}
        <div className="bg-primary/10 border border-primary/30 p-6 mb-6">
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm uppercase">Story Health Score</span>
            <span className="font-mono text-2xl font-bold text-primary">
              {auditResults.score || 75}/100
            </span>
          </div>
        </div>

        {/* Issues */}
        {auditResults.issues && auditResults.issues.length > 0 && (
          <div className="mb-6">
            <h4 className="font-mono text-sm uppercase text-destructive mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Issues Found
            </h4>
            <ul className="space-y-2">
              {auditResults.issues.map((issue, i) => (
                <li key={i} className="text-sm text-foreground bg-destructive/10 border border-destructive/30 p-3">
                  {issue}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Suggestions */}
        {auditResults.suggestions && auditResults.suggestions.length > 0 && (
          <div className="mb-6">
            <h4 className="font-mono text-sm uppercase text-primary mb-3 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Suggestions
            </h4>
            <ul className="space-y-2">
              {auditResults.suggestions.map((suggestion, i) => (
                <li key={i} className="text-sm text-muted-foreground bg-primary/5 border border-primary/20 p-3">
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Strengths */}
        {auditResults.strengths && auditResults.strengths.length > 0 && (
          <div className="mb-6">
            <h4 className="font-mono text-sm uppercase text-green-500 mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Strengths
            </h4>
            <ul className="space-y-2">
              {auditResults.strengths.map((strength, i) => (
                <li key={i} className="text-sm text-foreground bg-green-500/10 border border-green-500/30 p-3">
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Button
          onClick={onClose}
          variant="outline"
          className="w-full rounded-none font-mono uppercase tracking-widest text-xs mt-4"
        >
          Close
        </Button>
      </div>
    </div>
  );
}
